import { useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Order } from '../../types';

export interface PendingOrdersApi {
  pendingNewOrders: Order[];
  setPendingNewOrders: Dispatch<SetStateAction<Order[]>>;
  showNewOrdersBanner: boolean;
  setShowNewOrdersBanner: Dispatch<SetStateAction<boolean>>;
  clearPendingNewOrders: () => void;
  addPendingNewOrders: (orders: Order[]) => void;
  updatePendingOrder: (orderId: string, updates: Partial<Order>) => void;
}

export function usePendingOrders(): PendingOrdersApi {
  const [pendingNewOrders, setPendingNewOrders] = useState<Order[]>([]);
  const [showNewOrdersBanner, setShowNewOrdersBanner] = useState(false);

  const clearPendingNewOrders = useCallback(() => {
    setPendingNewOrders([]);
    setShowNewOrdersBanner(false);
  }, []);

  const addPendingNewOrders = useCallback((orders: Order[]) => {
    setPendingNewOrders(prev => {
      const existingIds = new Set(prev.map(o => o.id));
      const toAdd = orders.filter(o => !existingIds.has(o.id));
      if (toAdd.length === 0) return prev;
      return [...toAdd, ...prev];
    });
    setShowNewOrdersBanner(true);
  }, []);

  const updatePendingOrder = useCallback((orderId: string, updates: Partial<Order>) => {
    setPendingNewOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updates } : o));
  }, []);

  return {
    pendingNewOrders,
    setPendingNewOrders,
    showNewOrdersBanner,
    setShowNewOrdersBanner,
    clearPendingNewOrders,
    addPendingNewOrders,
    updatePendingOrder,
  };
}
